/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useEffect, useMemo, useRef, useState } from 'react';

import { useDeferredFlush, useLatestRef } from '@gitbook/util-react-hooks';

import { useNavigationState } from './NavigationProvider';
import { useNavigation } from './useNavigation';

interface SearchQueryCallbacks {
    open(this: void, newQuery?: string, askGitBook?: boolean): void;
    close(this: void): void;
    setQuery(this: void, query: string | undefined): void;
}

/**
 * Hook to manipulate the search query in the url
 */
export function useSearchQuery(): [string | undefined, SearchQueryCallbacks, boolean] {
    const { screen, overlay } = useNavigationState();
    const { navigate } = useNavigation();

    const current = overlay || screen;
    const { query, askAI = false } = current;

    // track the last non-undefined query
    const lastQuery = useRef(query);

    useEffect(() => {
        if (typeof query !== 'undefined') {
            lastQuery.current = query;
        }
    }, [query]);

    const callbacks = useMemo<SearchQueryCallbacks>(
        () => ({
            open(newQuery?: string, askGitBook = false) {
                navigate(
                    {
                        current: {
                            query: newQuery ?? lastQuery.current ?? '',
                            askAI: askGitBook ? true : undefined,
                        },
                    },
                    { replace: true }
                );
            },
            close() {
                navigate({ current: { query: undefined, askAI: undefined } }, { replace: true });
            },
            setQuery(query: string | undefined) {
                if (query === lastQuery.current) {
                    return;
                }

                navigate({ current: { query, askAI: undefined } }, { replace: true });
            },
        }),
        [navigate, lastQuery]
    );

    return [query, callbacks, askAI];
}

/**
 * Hook to get only the search controls without subscribing to query state changes.
 * Useful when you only need to trigger search actions but don't need to react to query changes.
 */
export function useSearchControls(): SearchQueryCallbacks {
    const { navigate } = useNavigation();

    return {
        open(newQuery?: string, askGitBook = false) {
            navigate(
                {
                    current: {
                        query: newQuery ?? '',
                        askAI: askGitBook ? true : undefined,
                    },
                },
                { replace: true }
            );
        },
        close() {
            navigate({ current: { query: undefined, askAI: undefined } }, { replace: true });
        },
        setQuery(query: string | undefined) {
            navigate({ current: { query, askAI: undefined } }, { replace: true });
        },
    };
}

/**
 * Similar to `useSearchQuery` but only update the url when the user stop typing.
 */
export function useDeferredSearchQuery(): [string | undefined, SearchQueryCallbacks, boolean] {
    const [deferredQuery, controls, askAI] = useSearchQuery();
    const [query, setQuery] = useState(deferredQuery);

    const latestDeferredQueryRef = useLatestRef(deferredQuery);

    // Update url to stay in sync with input
    const defer = useDeferredFlush(1000);
    useEffect(() => {
        if (query) {
            defer(() => controls.setQuery(query));
        } else {
            defer.cancel();
            if (latestDeferredQueryRef.current !== query) {
                controls.setQuery(query);
            }
        }
    }, [query]);

    // When url is changing, we reset the input if needed
    useEffect(() => {
        setQuery(deferredQuery);
    }, [typeof deferredQuery]);

    return [query, { ...controls, setQuery }, askAI];
}
